/**
 * Validate and extract a `returnTo` query param so a post-login navigate can
 * bounce the user back to a subdomain app (yc.<env>, care-planner.<env>) they were
 * redirected from. Open-redirect-safe: only same-host or strict
 * subdomain-of-current-host URLs are accepted. Anything else (cross-origin
 * to evil.com, javascript:, file:, malformed) returns null and the caller
 * falls back to the default landing route.
 */
import { deriveMainHost } from '@/api/mainAppUrl'

// Shared protocol gate for both validators below. HTTPS only — with a narrow
// localhost-dev exception: when the page itself is on http: (i.e. local dev),
// accept http: so the cross-subdomain flow can be tested without certs. In any
// deployed env the page is https:, so http: is still rejected and an attacker
// cannot use a crafted http: same-suffix URL to downgrade the navigation past
// HSTS gaps on a not-yet-pinned subdomain.
function parseWithSafeProtocol(raw: string | null): URL | null {
  if (!raw) return null
  let url: URL
  try {
    url = new URL(raw)
  } catch {
    return null
  }
  if (url.protocol === 'https:') return url
  if (url.protocol === 'http:' && window.location.protocol === 'http:') return url
  return null
}

// A hostname is in `anchor`'s family iff it IS the anchor or a strict
// subdomain of it (the literal-dot separator defeats suffix-spoofing like
// `usb.tricorder.icu.evil.com`). Shared by both validators — only the anchor
// differs (current host for login, main host for the cross-app handoff).
function inHostFamily(hostname: string, anchor: string): boolean {
  return hostname === anchor || hostname.endsWith('.' + anchor)
}

export function getSafeReturnTo(searchParams: URLSearchParams): string | null {
  // NOTE the casing: the LOGIN flow's wire param is camelCase `returnTo`
  // (historical, produced by mainAppLoginUrl). The session-handoff flow uses
  // snake_case `return_to` (RETURN_TO_PARAM, matching from_child /
  // parent_session) and validates with getSafeCrossAppReturnTo below — don't
  // wire one flow's param to the other's validator.
  const url = parseWithSafeProtocol(searchParams.get('returnTo'))
  if (!url) return null
  return inHostFamily(url.hostname, window.location.hostname) ? url.href : null
}

/**
 * Validate a raw `return_to` value carried on a CHILD sub-app URL, so the
 * child's "Return to chat" can land on the exact screen the run was launched
 * from (the Home workspace, the full-screen chat, another sub-app) instead of
 * always the full-screen parent session (the launched-from-Home return bug).
 *
 * Differs from getSafeReturnTo above in the host anchor: the child runs on a
 * SUB-APP host (alg.<env>), and the legitimate return target is the MAIN host
 * family — the current host minus this app's own subdomain (deriveMainHost)
 * or any subdomain of it (a sibling sub-app parent, e.g. a Care Planner launcher).
 * Anchoring on the current host would reject every one of those. Same
 * open-redirect posture otherwise: cross-origin, javascript:, file:, and
 * malformed values return null and the caller falls back to the parent
 * session URL.
 *
 * NOTE the casing: this validates the snake_case `return_to` handoff param
 * (RETURN_TO_PARAM in sessionRouting.ts) — NOT the login flow's camelCase
 * `returnTo`, which getSafeReturnTo above owns. See the note there.
 */
export function getSafeCrossAppReturnTo(raw: string | null): string | null {
  const url = parseWithSafeProtocol(raw)
  if (!url) return null
  return inHostFamily(url.hostname, deriveMainHost(window.location.hostname)) ? url.href : null
}
